import React from 'react'
import { Select, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import EyeOutlined from '@ant-design/icons/EyeOutlined'
import FileExcelOutlined from '@ant-design/icons/FileExcelOutlined'
import isNil from 'lodash/isNil'
import styles from './UploadReview.module.scss'
import {
  ClientOptionDto,
  InvoiceTemplate,
} from '../../../api/dto/invoice-delivery.dto'
import { TruncatedFileName } from '../TruncatedFileName'
import { INVOICE_TEMPLATE_LABELS } from './invoiceTemplates'
import { ReviewRow } from './reviewRows'
import {
  AmountInput,
  DateInput,
  OnEdit,
  renderAmountLines,
  renderDueDateLines,
  renderTemplateLines,
} from './reviewLines'
import {
  CellLines,
  CompanyName,
  FileLine,
  InputLine,
  RemoveRowButton,
  RowActions,
  RowIconButton,
  RowWarning,
  StackedCell,
} from './UploadReview.styles'

export const toTemplateOptions = (templates: readonly InvoiceTemplate[]) =>
  templates.map((template) => ({
    value: template,
    label: INVOICE_TEMPLATE_LABELS[template],
  }))

interface ReviewColumnHandlers {
  clients: ClientOptionDto[]
  templateOptions: ReturnType<typeof toTemplateOptions>
  onChooseClient: (key: string, clientId: string) => void
  onTemplateChange: (key: string, template: InvoiceTemplate) => void
  onEdit: OnEdit
  onPreview: (key: string) => void
  onRemove: (key: string) => void
}

/** Columns of the upload review: company and file, template, amount, due date, actions. */
export const buildReviewColumns = ({
  clients,
  templateOptions,
  onChooseClient,
  onTemplateChange,
  onEdit,
  onPreview,
  onRemove,
}: ReviewColumnHandlers): ColumnsType<ReviewRow> => {
  const clientOptions = clients.map((c) => ({ value: c.id, label: c.name }))
  return [
    {
      title: 'Company',
      key: 'company',
      width: 290,
      render: (_, row) => (
        <StackedCell>
          {isNil(row.matchedClient) ? (
            <>
              <Select
                showSearch
                optionFilterProp="label"
                value={row.client?.id}
                options={clientOptions}
                onChange={(clientId: string) =>
                  onChooseClient(row.key, clientId)
                }
                placeholder="Choose company"
                status={isNil(row.client) ? 'warning' : undefined}
                aria-label={`Company for ${row.file.name}`}
                className={styles.companySelect}
              />
              {isNil(row.client) ? (
                <RowWarning>
                  “{row.parsed?.companyName ?? 'No name on sheet'}” matched no
                  company
                </RowWarning>
              ) : null}
            </>
          ) : (
            <CompanyName title={row.matchedClient.name}>
              {row.matchedClient.name}
            </CompanyName>
          )}
          <FileLine>
            <FileExcelOutlined className={styles.fileIcon} aria-hidden="true" />
            <TruncatedFileName
              name={row.file.name}
              companyName={row.client?.name ?? row.parsed?.companyName}
            />
          </FileLine>
          {row.isDuplicate ? (
            <RowWarning>
              Another file in this upload has the same company and email
              template
            </RowWarning>
          ) : null}
        </StackedCell>
      ),
    },
    {
      title: 'Email template',
      key: 'template',
      width: 200,
      render: (_, row) => (
        <CellLines>
          <InputLine>
            <Select
              value={row.template}
              options={templateOptions}
              onChange={(template) => onTemplateChange(row.key, template)}
              status={row.isDuplicate ? 'warning' : undefined}
              aria-label={`Email template for ${row.file.name}`}
              className={styles.templateSelect}
            />
          </InputLine>
          {renderTemplateLines(row)}
        </CellLines>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 160,
      render: (_, row) => (
        <CellLines>
          <InputLine>
            <AmountInput
              value={row.values.amount}
              label={`Amount for ${row.file.name}`}
              onChange={(amount) => onEdit(row.key, { amount })}
            />
          </InputLine>
          {renderAmountLines(row, onEdit)}
        </CellLines>
      ),
    },
    {
      title: 'Due date',
      key: 'dueDate',
      width: 170,
      render: (_, row) => (
        <CellLines>
          <InputLine>
            <DateInput
              value={row.values.dueDate}
              label={`Due date for ${row.file.name}`}
              onChange={(dueDate) => onEdit(row.key, { dueDate })}
            />
          </InputLine>
          {renderDueDateLines(row)}
        </CellLines>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 84,
      align: 'right',
      render: (_, row) => (
        <RowActions>
          <Tooltip title="Preview email">
            <RowIconButton
              type="button"
              onClick={() => onPreview(row.key)}
              aria-label={`Preview the ${INVOICE_TEMPLATE_LABELS[row.template]} email for ${row.file.name}`}
            >
              <EyeOutlined />
            </RowIconButton>
          </Tooltip>
          <Tooltip title="Remove from this upload">
            <RemoveRowButton
              type="button"
              onClick={() => onRemove(row.key)}
              aria-label={`Remove ${row.file.name} from this upload`}
            >
              <CloseOutlined />
            </RemoveRowButton>
          </Tooltip>
        </RowActions>
      ),
    },
  ]
}
